import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './layouts/AppShell';
import { HomeRoute } from './routes/Home';
import { LoginRoute } from './routes/Login';
import { ForgotPasswordRoute } from './routes/ForgotPassword';
import { ResetPasswordRoute } from './routes/ResetPassword';
import { RegisterRoute } from './routes/Register';
import { SsoCallbackRoute } from './routes/SsoCallback';
import { NotFoundRoute } from './routes/NotFound';
import { RequireAuth } from './lib/auth';
import { PageSkeleton, RouteSpinner } from './components/PageSkeleton';
import { ScrollToTop } from './components/ScrollToTop';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';

/* ── Lazy-loaded routes ──────────────────────────────────────────
 *  Each route is a separate chunk so users only download what they
 *  visit. Login + AppShell stay in the main bundle since they're
 *  always needed.
 * ──────────────────────────────────────────────────────────────── */

const DashboardRoute = lazy(() =>
  import('./routes/Dashboard').then((m) => ({ default: m.DashboardRoute })),
);
const CalendarRoute = lazy(() =>
  import('./routes/Calendar').then((m) => ({ default: m.CalendarRoute })),
);
const PublishRoute = lazy(() =>
  import('./routes/Publish').then((m) => ({ default: m.PublishRoute })),
);
const AnalyticsRoute = lazy(() =>
  import('./routes/Analytics').then((m) => ({ default: m.AnalyticsRoute })),
);
const EngageRoute = lazy(() =>
  import('./routes/Engage').then((m) => ({ default: m.EngageRoute })),
);
const AutomationRulesRoute = lazy(() =>
  import('./routes/engage/AutomationRules').then((m) => ({
    default: m.AutomationRulesRoute,
  })),
);
const ListeningRoute = lazy(() =>
  import('./routes/Listening').then((m) => ({ default: m.ListeningRoute })),
);
const SeoRoute = lazy(() =>
  import('./routes/Seo').then((m) => ({ default: m.SeoRoute })),
);
const ReportsRoute = lazy(() =>
  import('./routes/Reports').then((m) => ({ default: m.ReportsRoute })),
);
const MediaLibraryRoute = lazy(() =>
  import('./routes/MediaLibrary').then((m) => ({ default: m.MediaLibraryRoute })),
);
const SettingsRoute = lazy(() =>
  import('./routes/Settings').then((m) => ({ default: m.SettingsRoute })),
);
const ResourcesRoute = lazy(() =>
  import('./routes/Resources').then((m) => ({ default: m.ResourcesRoute })),
);
const BioPublicRoute = lazy(() =>
  import('./routes/BioPublic').then((m) => ({ default: m.BioPublicRoute })),
);
const ChatRoute = lazy(() =>
  import('./routes/Chat').then((m) => ({ default: m.ChatRoute })),
);
const ProjectsRoute = lazy(() =>
  import('./routes/Projects').then((m) => ({ default: m.ProjectsRoute })),
);
const PlannerRoute = lazy(() =>
  import('./routes/Planner').then((m) => ({ default: m.PlannerRoute })),
);
const ProjectDetailRoute = lazy(() =>
  import('./routes/ProjectDetail').then((m) => ({ default: m.ProjectDetailRoute })),
);
const CrmRoute = lazy(() =>
  import('./routes/Crm').then((m) => ({ default: m.CrmRoute })),
);
const PressRoomRoute = lazy(() =>
  import('./routes/PressRoom').then((m) => ({ default: m.PressRoomRoute })),
);
const WorkspaceHubRoute = lazy(() =>
  import('./routes/WorkspaceHub').then((m) => ({ default: m.WorkspaceHubRoute })),
);
const AdminTokensRoute = lazy(() =>
  import('./routes/AdminTokens').then((m) => ({ default: m.AdminTokensRoute })),
);
const AdminLicensesRoute = lazy(() =>
  import('./routes/AdminLicenses').then((m) => ({ default: m.AdminLicensesRoute })),
);
const SuperAdminRoute = lazy(() =>
  import('./routes/SuperAdmin').then((m) => ({ default: m.SuperAdminRoute })),
);

/* ── Router ──────────────────────────────────────────────────────── */

export function App() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<RouteSpinner />}>
        <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/register" element={<RegisterRoute />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordRoute />} />
        <Route path="/auth/reset-password" element={<ResetPasswordRoute />} />
        <Route path="/auth/sso-callback" element={<SsoCallbackRoute />} />
        <Route
          path="/b/:slug"
          element={
            <Suspense fallback={<RouteSpinner />}>
              <BioPublicRoute />
            </Suspense>
          }
        />
        <Route
          path="/bio/:slug"
          element={
            <Suspense fallback={<RouteSpinner />}>
              <BioPublicRoute />
            </Suspense>
          }
        />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route
            path="/dashboard"
            element={
              <RouteErrorBoundary routeName="Dashboard">
                <Suspense fallback={<PageSkeleton />}>
                  <DashboardRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/workspace"
            element={
              <RouteErrorBoundary routeName="Workspace">
                <Suspense fallback={<PageSkeleton />}>
                  <WorkspaceHubRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/calendar"
            element={
              <RouteErrorBoundary routeName="Calendar">
                <Suspense fallback={<RouteSpinner />}>
                  <CalendarRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/publish/*"
            element={
              <RouteErrorBoundary routeName="Publish">
                <Suspense fallback={<RouteSpinner />}>
                  <PublishRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/analytics/*"
            element={
              <RouteErrorBoundary routeName="Analytics">
                <Suspense fallback={<RouteSpinner />}>
                  <AnalyticsRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/engage"
            element={
              <RouteErrorBoundary routeName="Engage">
                <Suspense fallback={<RouteSpinner />}>
                  <EngageRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/engage/automation"
            element={
              <RouteErrorBoundary routeName="Automation Rules">
                <Suspense fallback={<RouteSpinner />}>
                  <AutomationRulesRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/listening/*"
            element={
              <RouteErrorBoundary routeName="Listening">
                <Suspense fallback={<RouteSpinner />}>
                  <ListeningRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/seo/*"
            element={
              <RouteErrorBoundary routeName="SEO & GEO">
                <Suspense fallback={<RouteSpinner />}>
                  <SeoRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/reports/*"
            element={
              <RouteErrorBoundary routeName="Reports">
                <Suspense fallback={<RouteSpinner />}>
                  <ReportsRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/media-library"
            element={
              <RouteErrorBoundary routeName="Media">
                <Suspense fallback={<RouteSpinner />}>
                  <MediaLibraryRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/settings/*"
            element={
              <RouteErrorBoundary routeName="Settings">
                <Suspense fallback={<RouteSpinner />}>
                  <SettingsRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/resources/*"
            element={
              <RouteErrorBoundary routeName="Resources">
                <Suspense fallback={<RouteSpinner />}>
                  <ResourcesRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/projects"
            element={
              <RouteErrorBoundary routeName="Projects">
                <Suspense fallback={<RouteSpinner />}>
                  <ProjectsRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/planner/*"
            element={
              <RouteErrorBoundary routeName="Planner">
                <Suspense fallback={<RouteSpinner />}>
                  <PlannerRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/projects/:projectId/*"
            element={
              <RouteErrorBoundary routeName="Project Detail">
                <Suspense fallback={<RouteSpinner />}>
                  <ProjectDetailRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/crm/*"
            element={
              <RouteErrorBoundary routeName="CRM">
                <Suspense fallback={<RouteSpinner />}>
                  <CrmRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/media/*"
            element={
              <RouteErrorBoundary routeName="Media">
                <Suspense fallback={<RouteSpinner />}>
                  <PressRoomRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/chat"
            element={
              <RouteErrorBoundary routeName="Team Chat">
                <Suspense fallback={<RouteSpinner />}>
                  <ChatRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route path="/bio" element={<Navigate to="/settings/bio" replace />} />
          <Route
            path="/admin/tokens"
            element={
              <RouteErrorBoundary routeName="Admin Tokens">
                <Suspense fallback={<PageSkeleton />}>
                  <AdminTokensRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/admin/licenses"
            element={
              <RouteErrorBoundary routeName="Admin Licenses">
                <Suspense fallback={<PageSkeleton />}>
                  <AdminLicensesRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/super-admin/*"
            element={
              <RouteErrorBoundary routeName="Super Admin">
                <Suspense fallback={<PageSkeleton />}>
                  <SuperAdminRoute />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        </Route>
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
    </Suspense>
    </>
  );
}
